/**
 * Die **nachzuhaltenden Befunde**: was sich ansammelt, bevor v2 gebaut wird
 * (Spec v1.2, Stories 216 und 217; ADR-0012).
 *
 * Drei Festlegungen tragen sie:
 *
 * - **Die Zeile ist ein Vermerk und keine Stelle.** Zwei Menschen dürfen
 *   denselben Befund nachhalten wollen, und dass sie es tun, ist ein **Signal**
 *   und keine Dublette — zusammengefasst wäre genau diese Auskunft weg.
 * - **Die Zahl steht darüber**, als Gesamtzahl und je Kalendermonat der letzten
 *   zwölf. An ihr hängt die Bedingung, ob v2 überhaupt anfängt: Wie oft jemand
 *   etwas nachgehalten *haben will*, misst schärfer als die bloße Zahl der
 *   Befunde. Als Tabelle und nicht als Grafik — die Zahl soll ablesbar sein.
 * - **Eine leere Liste ist die Antwort und kein Ausfall.** Bleibt sie ein halbes
 *   Jahr leer, ist v2 beantwortet, bevor es gebaut wurde. Das steht an der
 *   Fläche und nicht nur in einer ADR.
 *
 * Sortiert wird im **Server**, nach Datum oder nach Person. Eine zweite,
 * eigene Ordnung in der Oberfläche wäre eine zweite Wahrheit über dieselbe
 * Liste.
 *
 * **Hier entsteht nichts.** Kein Vorschlag, kein Prüfauftrag, kein Modellaufruf
 * — der Vorgang ist v2 und hat dort sein eigenes Gatter (ADR-0012). Diese Fläche
 * liest.
 *
 * **Ohne Werkzeugleiste** (#41, #136): Der einzige Knopf ist der Filter „von mir
 * vermerkt", und der steht in der Filterleiste des Abschnitts, den er einengt —
 * er wählt aus, was zu sehen ist, und handelt nicht am ganzen Screen. Ohne einen
 * Knopf, der auf den Screen als Ganzen wirkt, bliebe die Leiste leer
 * (`docs/human/2026-08-27-werkzeugleiste-und-sektionen.md`).
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { Checkbox, Chip, DataTable } from "../design-system";
import type {
  FollowUpMark,
  FollowUpMarkOrder,
  FollowUpMarkQuery,
  FollowUpMarksApi,
} from "../api/client";
import { formatNumber, formatTimestamp } from "../formatting";
import { NumericCell, StrongCell } from "./cells";
import { UNTITLED_LABEL } from "./findings";
import { ListPage, ListSection } from "./list";
import { Notice, useLoaded } from "./loading";

/**
 * Der Knopf **„von mir vermerkt"** — der einzige Filter dieser Liste.
 *
 * **„Mein" heißt hier Urheberschaft:** Ich habe diesen Vermerk gesetzt. Das ist
 * beim Vermerk die richtige Lesart, weil er eine Willenserklärung einer Person
 * *ist*. Er heißt deshalb nicht „meine" und nicht „eigene": Im Befundvorrat
 * trägt „an meinem Code" die Betroffenheitsrelation (ADR-0028), und ein Filter,
 * der in zwei Listen zwei verschiedene Dinge heißt, ist genau der Fehler, den
 * jene ADR abwendet.
 *
 * **„An meinem Code" gibt es hier ausdrücklich nicht.** Er ergäbe die Liste
 * „welche meiner Fehler wollen Kollegen künftig maschinell gemeldet haben" —
 * die einzige Stelle, an der die Betroffenheitsrelation dem Betroffenen keine
 * Handlung anbietet: Ein Vermerk hat in v1.2 keinen Lebenszyklus, man kann ihn
 * weder annehmen noch bestreiten (ADR-0012). Derselbe Befund steht ohnehin im
 * Vorrat, und dort ist er Arbeit.
 */
const OWN_MARKS = "von mir vermerkt";

/** Was der Knopf nimmt — und was er ausdrücklich nicht nimmt. */
const OWN_MARKS_TITLE =
  "Nur die Vermerke, die Sie selbst gesetzt haben. „Mein“ heißt hier Urheberschaft: " +
  "Der Vermerk ist Ihre Willenserklärung. Nicht dasselbe wie „an meinem Code“ im " +
  "Befundvorrat — das sind die Befunde an den Revisionen, die Sie übergeben haben.";

const EMPTY =
  "Noch hat niemand einen Befund nachgehalten. Das ist kein Mangel dieser Liste, " +
  "sondern ihre Auskunft: Bleibt sie leer, ist die Frage beantwortet, ob sich das " +
  "Aufnehmen von Regeln überhaupt lohnt.";

/**
 * Dieselbe leere Liste, aber mit gesetztem Knopf — und deshalb ein anderer Satz.
 *
 * Der Text oben ist die tragende Aussage dieser Fläche: „niemand hält etwas
 * nach". Sie über einer selbst eingeschränkten Menge zu wiederholen, behauptete
 * sie über alle, obwohl nur die eigenen gezählt wurden.
 */
const EMPTY_MINE =
  "Sie haben noch keinen Befund nachgehalten. Was Kollegen nachgehalten haben, steht in " +
  `der vollständigen Liste — dafür den Knopf „${OWN_MARKS}“ abwählen.`;

const EXPLANATION =
  "Jede Zeile ist der Vermerk eines Menschen, dass die Erkennung für diesen Befund " +
  "künftig sensibilisiert sein soll — samt seiner Begründung. Es entsteht daraus " +
  "nichts: kein Vorschlag, kein Prüfauftrag, kein Modellaufruf. Zwei Vermerke am " +
  "selben Befund stehen als zwei Zeilen, denn dass zwei Menschen dasselbe wollen, " +
  "ist die Aussage.";

/**
 * Die Vorbelegung: was zuletzt aufgefallen ist, steht oben — und der Knopf ist
 * **aus**. Die Liste ist der Rückstand, mit dem v2 anfängt, und wer sie öffnet,
 * will zuerst den ganzen sehen.
 */
const START: FollowUpMarkQuery = { sort: "created_at", descending: true, markedBy: null };

/**
 * Welche Spalte welche Ordnung bestellt. Die übrigen Spalten sortieren nicht —
 * ihre Kopfzeile ist anklickbar, weil die Tabelle des Design-Systems jede
 * anklickbar macht, und ein Klick darauf tut schlicht nichts.
 */
const ORDERS: Record<string, FollowUpMarkOrder> = {
  person: "person",
  createdAt: "created_at",
};

export function FollowUpMarksView({
  client,
  onOpenMark,
}: {
  client: FollowUpMarksApi;
  /**
   * Der Sprung an den Befund: Er führt in die Revision, in der der Befund liegt
   * (ADR-0006, ADR-0019). Ein Vermerk an einem Befund ohne Revision — ein
   * Bestandsbefund an einer nie geänderten Datei — führt nirgendwohin, und die
   * Zeile sagt das, statt in eine geratene Revision zu springen.
   */
  onOpenMark: (mark: FollowUpMark) => void;
}): ReactElement {
  const [query, setQuery] = useState<FollowUpMarkQuery>(START);
  const { state, reload } = useLoaded(() => client.followUpMarks(query));

  const loaded = state.kind === "loaded" ? state.value : null;
  const marks = loaded?.marks ?? [];

  /**
   * Ein Klick auf eine Kopfzeile: dieselbe Spalte kehrt die Richtung um, eine
   * andere beginnt bei aufsteigend. Bestellt wird beim Server — deshalb gleich
   * neu geladen.
   */
  const sortBy = (column: string): void => {
    const order = ORDERS[column];

    if (order === undefined) {
      return;
    }

    setQuery(
      order === query.sort
        ? { ...query, sort: order, descending: !query.descending }
        : { ...query, sort: order, descending: false },
    );
    reload();
  };

  /** Eine Änderung am Filter — wie beim Sortieren wird beim Server neu bestellt. */
  const filterBy = (markedBy: FollowUpMarkQuery["markedBy"]): void => {
    setQuery({ ...query, markedBy });
    reload();
  };

  return (
    <ListPage>
      <ListSection
        title="Nachhalten über die Zeit"
        glyph="flag"
        first
        headerRight={
          loaded === null ? null : (
            <Chip>{`${formatNumber(loaded.total)} ${loaded.total === 1 ? "Vermerk" : "Vermerke"}`}</Chip>
          )
        }
        /*
          Die Leiste steht **hier** und nicht über der Liste unten: Sie schränkt
          auch die Zahlen ein, und ein Knopf, der eine Zahl über sich ändert,
          gehört neben diese Zahl und nicht darunter.
        */
        filters={
          <div
            role="group"
            aria-label="Filter"
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "var(--sp-4)",
              padding: "var(--sp-5) var(--sp-6)",
              borderBottom: "1px solid var(--line-f)",
            }}
          >
            {/*
              Der Hinweis hängt am Aufrufort und nicht am Baustein: Die Checkbox
              des Skills nimmt ihre Beschriftung als Text und legt das `<input>`
              selbst in ein `<label>` — ein Element darin, das einen Titel tragen
              könnte, gibt es nicht mehr. Der Satz ist zu lang für die
              Beschriftung und zu genau, um zu entfallen: Er sagt, dass „mein"
              hier Urheberschaft heißt.
            */}
            <span title={OWN_MARKS_TITLE}>
              <Checkbox
                checked={query.markedBy === "me"}
                label={OWN_MARKS}
                onChange={(marked) => {
                  filterBy(marked ? "me" : null);
                }}
              />
            </span>
          </div>
        }
      >
        <p
          style={{
            margin: 0,
            padding: "0 var(--sp-5) var(--sp-5)",
            maxWidth: "78ch",
            fontSize: "var(--fs-11)",
            lineHeight: "var(--lh-normal)",
            color: "var(--fg-6)",
            textWrap: "pretty",
          }}
        >
          {EXPLANATION}
        </p>

        {loaded === null ? null : (
          <DataTable
            columns={[
              { key: "month", label: "Monat", width: 140 },
              { key: "marks", label: "Vermerke", width: 120, align: "right" },
            ]}
            rows={loaded.months.map((one) => ({
              cells: {
                month: one.month,
                marks: <NumericCell>{formatNumber(one.marks)}</NumericCell>,
              },
            }))}
          />
        )}
      </ListSection>

      <ListSection title="Nachzuhaltende Befunde" glyph="list-plus">
        <div style={{ padding: "0 var(--sp-5)" }}>
          {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
          {state.kind === "failed" ? (
            <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
          ) : null}
          {loaded !== null && marks.length === 0 ? (
            <Notice text={query.markedBy === null ? EMPTY : EMPTY_MINE} glyph="flag" />
          ) : null}
        </div>

        {marks.length === 0 ? null : (
          <DataTable
            columns={[
              { key: "title", label: "Befund", maxWidth: 280 },
              { key: "path", label: "Datei", maxWidth: 240 },
              { key: "site", label: "Stelle", width: 140 },
              { key: "project", label: "Projekt", width: 160 },
              { key: "reason", label: "Begründung", maxWidth: 420 },
              { key: "person", label: "Person", width: 180 },
              { key: "createdAt", label: "Vermerkt am", width: 170 },
            ]}
            /*
              Die sortierte Spalte und ihre Richtung stehen seit #142 als ein
              Paar da und nicht mehr als zwei Eigenschaften nebeneinander — zwei
              Angaben, die nur zusammen etwas bedeuten, waren zwei Gelegenheiten,
              die eine zu setzen und die andere zu vergessen. Sortiert wird
              weiter im Server; hier steht nur, was dort gilt.
            */
            sort={{
              key: query.sort === "person" ? "person" : "createdAt",
              dir: query.descending ? "desc" : "asc",
            }}
            onSort={sortBy}
            rows={marks.map((one) => ({
              cells: {
                // Der Titel führt die Zeile; wo keiner abgelegt ist, steht das da
                // und keiner wird erfunden (Glossar, „Titel").
                title: (
                  <StrongCell>
                    {one.finding.title === "" ? UNTITLED_LABEL : one.finding.title}
                  </StrongCell>
                ),
                path: fileOf(one.finding.path),
                site: siteOf(one),
                project: one.projectName,
                reason: one.reason,
                person: one.person,
                createdAt: formatTimestamp(one.createdAt),
              },
            }))}
            onRowClick={(_row, index) => {
              const found = marks[index];

              if (found !== undefined) {
                onOpenMark(found);
              }
            }}
          />
        )}
      </ListSection>
    </ListPage>
  );
}

/** Der Dateiname ohne seinen Pfad — die Spalte ist schmal, der Pfad steht daneben. */
function fileOf(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

/**
 * Die **Stelle** in einer Zelle: Revision und Zeilenbereich (ADR-0006).
 *
 * Eine einzelne Zeile steht ohne Bereich da: „5–5" läse sich wie eine Spanne,
 * die es nicht gibt.
 */
function siteOf(mark: FollowUpMark): string {
  const { revisionNumber, startLine, endLine } = mark.finding;
  const lines = startLine === endLine ? String(startLine) : `${String(startLine)}–${String(endLine)}`;

  return `r${String(revisionNumber)} · ${lines}`;
}
